"use client";

import { composeRefs } from "@radix-ui/react-compose-refs";
import React from "react";
import { withRefProp } from "../../../utils/withRefProp";
import Button from "../../common/Button/Button";
import TextInput, { type TextInputProps } from "../TextInput/TextInput";
import styles from "./DateTimeInput.module.scss";
import { PickerPopover } from "./PickerPopover";
import {
  TimeList,
  type TimePreset,
  type TimePresetLayout,
  buildTimeOptions,
  findTimeOptionIndex,
  getColumnSwitchIndex,
  getNextTimeOptionIndexInLayout,
  getTimeOptionId,
} from "./TimeList";
import { type TimeOfDay, formatTime, getLocaleHour12, parseTime, timeToValue } from "./dateTimeFormat";
import { useCommittedText } from "./useCommittedText";

export { TIME_PRESETS, type TimePreset } from "./TimeList";

const NOW = "now";

export interface TimeInputProps
  extends Omit<TextInputProps, "value" | "onValueChange" | "onChange" | "type" | "clearable" | "onClear"> {
  /** 24 hour "HH:mm" or "HH:mm:ss", "" when empty, or "now" when `allowNow` is set. */
  value?: string | null;
  onValueChange?: (value: string) => void;
  /** 12 hour ("9:00 am") or 24 hour ("09:00") display. Defaults to the locale. Typing accepts either. */
  hour12?: boolean;
  /** BCP 47 locale used for the default `hour12`. Defaults to the browser locale. */
  locale?: string;
  /**
   * Minutes between options in the list (default 15). Pass null to list only the presets; with no
   * presets either, the input is a plain typed field. Typed values are never snapped to the step.
   */
  stepMinutes?: number | null;
  /** Shortcuts listed with the stepped times. Define your own or use TIME_PRESETS. */
  presets?: TimePreset[];
  /** "side" (default): presets in a column beside the times. "stacked": presets above them. */
  presetLayout?: TimePresetLayout;
  /** Always show and emit seconds ("HH:mm:ss"). Seconds are otherwise shown only when non-zero. */
  showSeconds?: boolean;
  /** Show a "Now" toggle and accept "now" as typed input. */
  allowNow?: boolean;
  clearable?: boolean;
  onClear?: () => void;
}

const TimeInput = withRefProp(
  ({
    value,
    onValueChange,
    hour12: hour12Prop,
    locale,
    stepMinutes = 15,
    presets,
    presetLayout = "side",
    showSeconds = false,
    allowNow = false,
    clearable,
    onClear,
    clearAriaLabel,
    disabled,
    error,
    placeholder,
    endAdornment,
    onBlur,
    onKeyDown,
    onClick,
    ref,
    ...otherProps
  }: TimeInputProps) => {
    const listId = React.useId();
    const inputRef = React.useRef<HTMLInputElement | null>(null);
    const [open, setOpen] = React.useState(false);
    const [activeIndex, setActiveIndex] = React.useState(-1);
    // True once the user moves through the list with the keyboard; until then Enter commits the typed text.
    const navigatedRef = React.useRef(false);
    const hour12 = React.useMemo(() => hour12Prop ?? getLocaleHour12(locale), [hour12Prop, locale]);
    const options = React.useMemo(() => buildTimeOptions(stepMinutes, presets), [stepMinutes, presets]);

    const isNow = allowNow && value === NOW;
    const time = React.useMemo(() => (value && value !== NOW ? parseTime(value) : null), [value]);

    const emit = (next: string) => {
      if (next !== (value ?? "")) onValueChange?.(next);
    };

    const text = useCommittedText<TimeOfDay | typeof NOW>({
      value: isNow ? NOW : time,
      format: (next) => (next === NOW ? NOW : formatTime(next, { hour12, showSeconds })),
      parse: (input) => (allowNow && input.trim().toLowerCase() === NOW ? NOW : parseTime(input)),
      onCommit: (next) => emit(next === null ? "" : next === NOW ? NOW : timeToValue(next, showSeconds)),
    });

    const hasList = options.length > 0;

    const openList = () => {
      if (disabled || !hasList) return;
      navigatedRef.current = false;
      setActiveIndex(findTimeOptionIndex(options, text.error ? null : (parseTime(text.text) ?? time)));
      setOpen(true);
    };
    const closeList = () => {
      setOpen(false);
      setActiveIndex(-1);
    };

    const select = (next: TimeOfDay) => {
      text.reset();
      emit(timeToValue(next, showSeconds));
      closeList();
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      onKeyDown?.(event);
      if (event.defaultPrevented) return;

      if (!open) {
        if (event.key === "ArrowDown" || event.key === "ArrowUp") {
          event.preventDefault();
          openList();
        } else if (event.key === "Enter") {
          text.commit();
        }
        return;
      }

      // Left/Right only switch columns once the user is moving through the list, so they still move
      // the caret while typing.
      const columnIndex = navigatedRef.current
        ? getColumnSwitchIndex(event.key, activeIndex, options, presetLayout)
        : null;
      const nextIndex = columnIndex ?? getNextTimeOptionIndexInLayout(event.key, activeIndex, options, presetLayout);
      if (nextIndex !== null) {
        event.preventDefault();
        navigatedRef.current = true;
        setActiveIndex(nextIndex);
        return;
      }

      if (event.key === "Enter") {
        event.preventDefault();
        if (navigatedRef.current && options[activeIndex]) select(options[activeIndex].time);
        else {
          text.commit();
          closeList();
        }
      } else if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        closeList();
      }
    };

    const handleTextChange = (next: string) => {
      text.change(next);
      navigatedRef.current = false;
      // Typing moves the highlight to the nearest option as a hint; Enter still commits the text.
      if (open) {
        const parsed = parseTime(next);
        setActiveIndex(parsed ? findTimeOptionIndex(options, parsed) : -1);
      }
    };

    const toggleNow = () => {
      text.reset();
      emit(isNow ? "" : NOW);
    };

    const handleClear = () => {
      text.reset();
      emit("");
      onClear?.();
    };

    return (
      <PickerPopover
        open={open}
        onOpenChange={(next) => (next ? openList() : closeList())}
        anchor={
          <div
            className={styles.anchor}
            data-full-width={otherProps.fullWidth ? "true" : "false"}
          >
            <TextInput
              {...otherProps}
              ref={composeRefs(inputRef, ref)}
              value={text.text}
              onValueChange={(next) => handleTextChange(next ?? "")}
              onClick={(event) => {
                onClick?.(event);
                if (open) closeList();
                else openList();
              }}
              onKeyDown={handleKeyDown}
              onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
                text.commit();
                closeList();
                onBlur?.(event);
              }}
              role={hasList ? "combobox" : undefined}
              aria-autocomplete={hasList ? "none" : undefined}
              aria-haspopup={hasList ? "listbox" : undefined}
              aria-expanded={hasList ? open : undefined}
              aria-controls={open ? listId : undefined}
              aria-activedescendant={open && activeIndex >= 0 ? getTimeOptionId(listId, activeIndex) : undefined}
              aria-invalid={error || text.error || undefined}
              disabled={disabled}
              error={error || text.error}
              placeholder={placeholder ?? (hour12 ? "9:00 am" : "09:00")}
              clearable={clearable}
              onClear={handleClear}
              clearAriaLabel={clearAriaLabel}
              autoComplete="off"
              endAdornment={
                <>
                  {endAdornment}
                  {allowNow ? (
                    <Button
                      type="button"
                      size="xs"
                      variant="outline"
                      intent="secondary"
                      label="Now"
                      selected={isNow}
                      aria-pressed={isNow}
                      onClick={toggleNow}
                      disabled={disabled}
                    />
                  ) : null}
                </>
              }
            />
          </div>
        }
      >
        <TimeList
          id={listId}
          options={options}
          value={time}
          activeIndex={activeIndex}
          onActiveIndexChange={setActiveIndex}
          onSelect={select}
          hour12={hour12}
          showSeconds={showSeconds}
          presetLayout={presetLayout}
        />
      </PickerPopover>
    );
  },
);

export default TimeInput;
